import { useTranslation } from "react-i18next"
import { Card, CardBody, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { Tag } from "@/components/ui/tag"
import { BetterDocExchangeTable } from "@/modules/admin/better-doc/components/better-doc-exchange-table"
import { BetterDocField } from "@/modules/admin/better-doc/components/better-doc-field"
import { BetterDocLiveRecommendationCard } from "@/modules/admin/better-doc/components/better-doc-live-recommendation-card"
import { BetterDocMedicTable } from "@/modules/admin/better-doc/components/better-doc-medic-table"
import { BetterDocMsnProgressCard } from "@/modules/admin/better-doc/components/better-doc-msn-progress-card"
import { BetterDocStepTimeline } from "@/modules/admin/better-doc/components/better-doc-step-timeline"
import { EMPTY_VALUE_PLACEHOLDER } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { formatBetterDocDateTime, formatBetterDocValue } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocSessionDetail } from "@/modules/admin/better-doc/services/betterDocSessionQuery"
import { BetterDocLiveRecommendationState } from "@/modules/admin/better-doc/types/BetterDocLiveRecommendationState"
import { BetterDocSessionActionOutcome } from "@/modules/admin/better-doc/types/BetterDocSessionActionOutcome"

type BetterDocSessionDetailContentProps = {
  detail: BetterDocSessionDetail
  actionOutcome: BetterDocSessionActionOutcome | null
  onActionCompleted: (outcome: BetterDocSessionActionOutcome) => void
  liveRecommendationState: BetterDocLiveRecommendationState
  onLiveRecommendationLoad: (recommendationId: string) => void
}

const readLiveBetterDocState = (liveRecommendationState: BetterDocLiveRecommendationState) => {
  switch (liveRecommendationState.status) {
    case "loaded":
      return liveRecommendationState.data.recommendation?.state ?? null
    case "loading":
      return liveRecommendationState.previousData?.recommendation?.state ?? null
    case "idle":
    case "failed":
      return null
  }
}

export const BetterDocSessionDetailContent = ({
  detail,
  actionOutcome,
  onActionCompleted,
  liveRecommendationState,
  onLiveRecommendationLoad,
}: BetterDocSessionDetailContentProps) => {
  const { t } = useTranslation()
  const { session, steps, recommendedMedics, exchanges } = detail
  const liveBetterDocState = readLiveBetterDocState(liveRecommendationState)

  return (
    <div className="flex flex-col gap-6" data-testid="better-doc-admin-session-detail-content">
      <Card data-testid="better-doc-admin-session-header">
        <CardHeader>
          <CardHeaderTitle className="flex flex-wrap items-center gap-2">
            {t("admin.betterDoc.sessionDetail.title", { sessionId: session.sessionId })}
            {session.isTestCase ? (
              <Tag type="attention" hasIcon={false} label={t("admin.betterDoc.testCaseTag")} />
            ) : null}
            {session.betterDocState === null ? null : (
              <Tag
                hasIcon={false}
                label={getBetterDocEnumLabel(t, "recommendationState", session.betterDocState)}
                data-testid="better-doc-admin-session-state"
              />
            )}
          </CardHeaderTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-3 gap-4 p-4 pt-0">
          <BetterDocField
            label={t("admin.betterDoc.sessionDetail.partnerSessionId")}
            value={session.partnerSessionId}
            testId="better-doc-admin-session-partner-session-id"
            copyText={session.partnerSessionId}
          />
          <BetterDocField
            label={t("admin.betterDoc.sessionDetail.partnerPatientId")}
            value={session.partnerPatientId}
            testId="better-doc-admin-session-partner-patient-id"
            copyText={session.partnerPatientId}
          />
          <BetterDocField
            label={t("admin.betterDoc.sessionDetail.progressNoteId")}
            value={formatBetterDocValue(session.progressNoteId)}
            testId="better-doc-admin-session-progress-note-id"
          />
          <BetterDocField
            label={t("admin.betterDoc.sessionDetail.recommendationId")}
            value={formatBetterDocValue(session.recommendationId)}
            testId="better-doc-admin-session-recommendation-id"
            copyText={session.recommendationId ?? undefined}
          />
          <BetterDocField
            label={t("admin.betterDoc.sessionDetail.caseId")}
            value={formatBetterDocValue(session.caseId)}
            testId="better-doc-admin-session-case-id"
            copyText={session.caseId ?? undefined}
          />
          <BetterDocField
            label={t("admin.betterDoc.sessionDetail.navigationType")}
            value={
              session.navigationType === null
                ? EMPTY_VALUE_PLACEHOLDER
                : getBetterDocEnumLabel(t, "navigationType", session.navigationType)
            }
            testId="better-doc-admin-session-navigation-type"
          />
          <BetterDocField
            label={t("admin.betterDoc.insuranceCountry.label")}
            value={
              session.insuranceCountry === null
                ? EMPTY_VALUE_PLACEHOLDER
                : getBetterDocEnumLabel(t, "insuranceCountry", session.insuranceCountry)
            }
            testId="better-doc-admin-session-insurance-country"
          />
          <BetterDocField
            label={t("admin.betterDoc.sessionDetail.testCaseScenario")}
            value={
              session.testCaseScenario === null
                ? EMPTY_VALUE_PLACEHOLDER
                : getBetterDocEnumLabel(t, "testCaseScenario", session.testCaseScenario)
            }
            testId="better-doc-admin-session-test-case-scenario"
          />
          <BetterDocField
            label={t("admin.betterDoc.sessionDetail.createdBy")}
            value={session.createdBy}
            testId="better-doc-admin-session-created-by"
          />
          <BetterDocField
            label={t("admin.betterDoc.sessionDetail.createdAt")}
            value={formatBetterDocDateTime(session.createdAt)}
            testId="better-doc-admin-session-created-at"
          />
          <BetterDocField
            label={t("admin.betterDoc.sessionDetail.updatedAt")}
            value={formatBetterDocDateTime(session.updatedAt)}
            testId="better-doc-admin-session-updated-at"
          />
        </CardBody>
      </Card>
      {session.navigationType === "MSN" ? (
        <BetterDocMsnProgressCard
          detail={detail}
          liveBetterDocState={liveBetterDocState}
          actionOutcome={actionOutcome}
          onActionCompleted={onActionCompleted}
        />
      ) : null}
      <BetterDocLiveRecommendationCard
        recommendationId={session.recommendationId}
        liveRecommendationState={liveRecommendationState}
        onLoad={onLiveRecommendationLoad}
      />
      <section className="flex flex-col gap-2">
        <h3 className="text-on-surface text-base font-medium">{t("admin.betterDoc.sessionDetail.steps")}</h3>
        <BetterDocStepTimeline steps={steps} />
      </section>
      <section className="flex flex-col gap-2">
        <h3 className="text-on-surface text-base font-medium">{t("admin.betterDoc.sessionDetail.exchanges")}</h3>
        <BetterDocExchangeTable
          exchanges={exchanges}
          emptyMessage={t("admin.betterDoc.sessionDetail.noExchanges")}
          testId="better-doc-admin-session-exchange-table"
        />
      </section>
      <section className="flex flex-col gap-2">
        <h3 className="text-on-surface text-base font-medium">
          {t("admin.betterDoc.sessionDetail.recommendedMedics")}
        </h3>
        <BetterDocMedicTable medics={recommendedMedics} />
      </section>
    </div>
  )
}
